import type { FC, ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at lines 75, 78, 90 (studio.home.counter.${type}, studio.home.counter.empty.${type}, studio.home.counter.manage|submit.${type}; type in unpublished|published):
//   studio.home.counter.unpublished
//   studio.home.counter.published
//   studio.home.counter.empty.unpublished
//   studio.home.counter.empty.published
//   studio.home.counter.manage.unpublished
//   studio.home.counter.manage.published
//   studio.home.counter.submit.unpublished
//   studio.home.counter.submit.published
import { sanitizeHref, Url } from '@wix/da-url';
import {
  BiData,
  BiEvent,
  BiContextProvidedValue,
  SeeAllClickBiEvent,
  SubmitButtonClickBiEvent,
} from '@wix/da-bi/pkg/events';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { type Props as IconProps } from '@wix/da-ds/pkg/Icons/IconWrap';
import ArrowRight from '@wix/da-ds/pkg/Icons/16x16/ArrowRight';
import StudioDraft from '@wix/da-ds/pkg/Icons/24x24/StudioDraft';
import StudioPublished from '@wix/da-ds/pkg/Icons/24x24/StudioPublished';
import UploadIcon from '@wix/da-ds/pkg/Icons/16x16/Upload';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';

import s from './CounterBlock.scss';

export interface Props {
  type: string;
  value: number;
}

const CounterBlock: FC<Props> = ({ type, value }) => {
  const { t } = useTranslation();

  let url: string,
    emptyUrl: string,
    Icon: ComponentType<IconProps>,
    EmptyIcon: ComponentType<IconProps>,
    biData: BiEvent,
    emptyBiData: BiEvent;

  switch (type) {
    case 'unpublished':
      url = Url.studio();
      emptyUrl = Url.studio();
      Icon = StudioDraft;
      EmptyIcon = UploadIcon;
      biData = BiData<SeeAllClickBiEvent>({
        evid: 106,
        sectionname: BiContextProvidedValue,
        sectiontype: 'unpublished',
      });
      emptyBiData = biData;
      break;
    case 'published':
      url = Url.studio({ section: 'published' });
      emptyUrl = Url.studio({ openSubmitForm: true });
      Icon = StudioPublished;
      EmptyIcon = AddIcon;
      biData = BiData<SeeAllClickBiEvent>({
        evid: 106,
        sectionname: BiContextProvidedValue,
        sectiontype: type,
      });
      emptyBiData = BiData<SubmitButtonClickBiEvent>({
        evid: 704,
        sectionname: BiContextProvidedValue,
      });
      break;
    default:
      return null;
  }

  return (
    <div className={s['root']}>
      <p className={s['count']}>
        {value ? (
          <>
            <strong>{value}</strong>
            <span>{t(`studio.home.counter.${type}`)}</span>
          </>
        ) : (
          <span>{t(`studio.home.counter.empty.${type}`)}</span>
        )}
      </p>
      <TextButton
        size="medium"
        variant="main"
        className={s['link']}
        href={sanitizeHref(value ? url : emptyUrl)}
        icon={value ? undefined : EmptyIcon}
        postfix={value ? ArrowRight : undefined}
        biData={value ? biData : emptyBiData}
      >
        {t(value ? `studio.home.counter.manage.${type}` : `studio.home.counter.submit.${type}`)}
      </TextButton>
      <BiLink
        className={s['icon']}
        href={sanitizeHref(url)}
        rel="noreferrer nofollow"
        biData={biData}
      >
        <Icon disableDefaultColors />
      </BiLink>
    </div>
  );
};
CounterBlock.displayName = 'CounterBlock';

export default CounterBlock;
